<template>
    <div v-if="isVisible"
         ref="marketingComponentRef"
         class="countdown-container">
        <div class="close-button-wrapper">
            <a :href="props.counter.destination_url"
               class="x-misclick-area"
               :aria-label="props.counter.title"
               data-category="countdown-visit-site-x"
               target="_blank"
               rel="nofollow noopener noindex" />
            <button id="hide-countdown"
                    aria-label="Hide countdown"
                    @click="closeCountdown()">
                <i class="fa-solid fa-xmark" />
            </button>
        </div>
        <div class="container">
            <div class="row align-items-center">
                <div class="col-12 col-md-2 text-block p-0 text-center">
                    <a :href="props.counter.destination_url"
                       target="_blank"
                       rel="nofollow noopener noindex"
                       data-category="countdown-visit-site"
                       :aria-label="props.counter.title">
                        <img loading="lazy"
                             width="100%"
                             height="100%"
                             :src="asset_cdn(props.counter.image.url)"
                             class="py-2 d-none d-md-inline"
                             :alt="props.counter.title"
                             :title="props.counter.title">
                    </a>
                </div>
                <div class="col-12 col-md-4 text-block p-0 pt-2">
                    <a :href="props.counter.destination_url"
                       target="_blank"
                       rel="nofollow noopener noindex"
                       data-category="countdown-visit-site">
                        <p class="h4">
                            <strong>{{ props.counter.title }}</strong>
                            {{ props.counter.subtitle }}
                        </p>
                    </a>
                </div>
                <div class="col-12 col-md-3 text-center align-items-center p-0">
                    <MarketingCampaignCountdownTimer v-if="props.counter.is_timer_enabled"
                                                     :remainingTime="props.counter.remaining_time" />
                </div>
                <div class="col-12 col-md-2 text-center">
                    <a :href="props.counter.destination_url"
                       target="_blank"
                       rel="nofollow noopener noindex"
                       class="countdown-btn btn btn-primary"
                       data-category="countdown-visit-site">
                        {{ props.counter.button_text }}
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {
        computed, onMounted, ref,
    } from 'vue';
    import { MarketingCampaignCounter } from './types/types';
    import MarketingCampaignCountdownTimer from './MarketingCampaignCountdownTimer.vue';
    import { asset_cdn } from '../helpers/helpers';
    import { isGivenHtmlType } from '@/js/helpers/type-guards/isGivenHtmlType';
    import { CustomEventsEnum } from '@/js/constants/customEvents';
    import { useEventListener } from '@/js/composables/useEventListener';

    interface Props {
        counter: MarketingCampaignCounter;
    }
    const props = defineProps<Props>();

    const STORAGE_VALUE = 'hidden';
    const STORAGE_NAME = 'marketing-countdown';
    const COOKIE_BAR_ENABLED_CLASS = 'cookie-bar-enabled';
    const COOKIE_BAR_CLASS_SELECTOR = '.cookie-bar';
    const COOKIE_BAR_IS_HIDDEN_CLASS = 'cookie-bar--is-hidden';
    const COUNTDOWN_CONTAINER_CLASS_SELECTOR = '.countdown-container';

    const textColor = props.counter.styling.global.text_color;
    const backgroundColor = props.counter.styling.global.background_color;
    const buttonTextColor = props.counter.styling.button.text_color;
    const buttonBackgroundColor = props.counter.styling.button.background_color;

    const hidden = ref(false);
    const marketingComponentRef = ref<HTMLDivElement | null>(null);
    const offsetElements = ref<HTMLCollectionOf<Element> | null>(null);

    const isVisible = computed(() => typeof window !== 'undefined' && !hidden.value && window.localStorage.getItem(STORAGE_NAME) !== STORAGE_VALUE);

    const setElementsOffset = (offset: number) => {
        if (offsetElements.value === null) {
            offsetElements.value = document.getElementsByClassName('countdown-offset');
        }

        if (offsetElements.value) {
            for (let i = 0; i < offsetElements.value.length; i++) {
                const htmlElement = offsetElements.value.item(i) as HTMLElement;
                if (htmlElement) {
                    htmlElement.style.bottom = `${offset}px`;
                }
            }
        }
    };

    const addElementsOffset = () => {
        if (!isVisible.value) {
            return;
        }

        const offset = marketingComponentRef.value?.offsetHeight ?? 0;

        setElementsOffset(offset);
    };

    const closeCountdown = () => {
        hidden.value = true;
        window.localStorage.setItem(STORAGE_NAME, STORAGE_VALUE);
        setElementsOffset(0);
    };

    const checkCookieBarVisibility = () => {
        const cookieBar = document.querySelector(COOKIE_BAR_CLASS_SELECTOR);
        const countdownContainer = document.querySelector(COUNTDOWN_CONTAINER_CLASS_SELECTOR);

        if (cookieBar && countdownContainer) {
            if (cookieBar.classList.contains(COOKIE_BAR_IS_HIDDEN_CLASS)) {
                countdownContainer.classList.remove(COOKIE_BAR_ENABLED_CLASS);
            } else {
                countdownContainer.classList.add(COOKIE_BAR_ENABLED_CLASS);
            }
        }
    };

    const handleCookieBarClose = () => {
        if (!marketingComponentRef.value) {
            return;
        }

        if (isGivenHtmlType<HTMLDivElement>(marketingComponentRef.value, HTMLDivElement)) {
            const classList = marketingComponentRef.value.classList;
            classList.remove(COOKIE_BAR_ENABLED_CLASS);
        }
    };

    onMounted(() => {
        if (isVisible.value) {
            checkCookieBarVisibility();
            addElementsOffset();
        }
    });

    useEventListener(window, CustomEventsEnum.COOKIE_BAR_CLOSED, handleCookieBarClose);
    useEventListener(window, 'resize', addElementsOffset);
</script>

<style scoped lang="scss">
.countdown-container {
    background-color: v-bind(backgroundColor) !important;

    .h4 {
        color: v-bind(textColor) !important;
    }
}

.countdown-btn {
    animation: glow-pulse 2s infinite;
    background-color: v-bind(buttonBackgroundColor) !important;
    color: v-bind(buttonTextColor) !important;
}

@keyframes glow-pulse {
    0% {
        box-shadow: 0 0 v-bind(buttonBackgroundColor);
    }
    70% {
        box-shadow: 0 0 0 15px transparent;
    }
    100% {
        box-shadow: 0 0 0 25px transparent;
    }
}

.close-button-wrapper {
    position: relative;

    .x-misclick-area {
        position: absolute;
        z-index: 1;
        top: -10px;
        left: -20px;
        width: 50px;
        height: 40px;
        background: transparent;
    }

    button {
        z-index: 2;
    }
}
</style>
